/**
 * État de la partie côté UI + accès au moteur (via l'adaptateur). Aucune valeur métier ici : la seed par défaut est
 * lue dans les params (`default_seed`) ou dérivée de l'année de base.
 */
import type { Choices, Measure, Programme, Timeline } from "../../engine/src/types.js";
import type { Content, UiFinancing, UiGameEvent, UiProgramme } from "./content-types.js";
import { engineProgramme, getEngine, missionCutGroups, variantCost, type AttributionItem, type EngineApi, type EngineContext, type Scorecard, type ScorecardExplanation, type SimMode } from "./engine-adapter.js";
import { checkEstimated, decodeEstimate, encodeEstimate, estimateBounds, isIaMeasure, MAX_SHARED_ESTIMATES, type CompactEstimate, type EstimateBounds } from "./estimated.js";
import { neutralityHit, neutralityNotice } from "./neutrality.js";
import { applyOverrides, sanitizeOverrides } from "./params-info.js";
import { codeTable, idCode } from "./share.js";
import { track } from "./telemetry.js";

/** Écrans v0.2 (ADR 0006). `verdict` (v0.1) est un alias de `resultat`. */
export type Screen = "existe" | "programme" | "financement" | "mandat" | "resultat";
/** Écrans hors boucle, accessibles à tout moment (ADR 0011). */
export type SideScreen = "hypotheses";
export type KpiView = "public" | "expert";
/** Comparaison affichée par le « Pourquoi ? » du bilan (ADR 0015). */
export type ScView = "trend" | "plf";
/** Métrique de tri du « Pourquoi ? ». */
export type AttrMetric = "d_deficit_pct" | "d_debt_pct" | "d_unemployment_pt" | "d_purchasing_power" | "d_growth_pt";

/** Une décision du mandat : événement déclenché par le moteur, dans l'ordre chronologique. */
export interface Decision {
  id: string;
  year: number;
  /** Rang 1-based dans la liste courante des événements déclenchés. */
  index: number;
  total: number;
  event: UiGameEvent;
}

export interface GameState {
  programme: UiProgramme;
  financing: UiFinancing;
  choices: Choices;
  seed: number;
  /** Budget voté : programme et financement verrouillés. */
  voted: boolean;
  /** Index (0-based) de la dernière année du mandat affichée comme réalisée ; -1 avant le vote. */
  yearIndex: number;
  kpiView: KpiView;
  attrMetric: AttrMetric;
  /** Hypothèses du moteur modifiées par le joueur (écran Hypothèses), dans leur fourchette publiée. */
  paramOverrides: Record<string, number>;
  /**
   * Mode réaliste « sans mécaniques politiques non sourcées » (ADR 0015) : pas de censure ni de crise de dette par l'indice de
   * confiance (conventions de jeu). Défaut : activé.
   */
  realistic: boolean;
  /** Priorités du joueur pour le bilan (indicateur → poids), seulement celles qui diffèrent du poids par défaut. */
  weights: Record<string, number>;
  /** « Pourquoi ? » du bilan : comparaison et indicateur affichés. */
  scView: ScView;
  scIndicator: string;
}

/**
 * Lien de partage. v0.1 : `{p, h, f, c, ch, s, y}` ; v0.2 ajoute `v: 2` et `va` (variantes) ; v0.3 (`v: 3`, ADR 0011) :
 * identifiants de mesures et de variantes remplacés par un code opaque (`idCode`), `m` (économies par mission, %) et
 * `hp` (hypothèses modifiées) ; v0.4 (`v: 4`, ADR 0015) : `rm` (mode réaliste) et `w` (priorités du bilan). Un lien v0.1–v0.3 reste
 * lisible : champs absents = défaut, `y ≥ 0` = budget voté, rejoué avec les conventions de jeu de l'époque (`rm` absent = 0).
 */
export interface SharePayload {
  v?: number;
  p: string[];
  h: Record<string, number>;
  f: Record<string, number>;
  c: number;
  ch: Choices;
  s: number;
  y: number;
  va?: Record<string, string>;
  /** Mesures dictées chiffrées par IA présentes dans le programme, forme compacte (ui/src/estimated.ts). */
  ia?: CompactEstimate[];
  /** Économies ciblées par groupe de missions (contrat v0.3 supposé `financing.mission_cuts`). */
  m?: Record<string, number>;
  /** Hypothèses modifiées (id de paramètre → valeur). */
  hp?: Record<string, number>;
  /** v4 (ADR 0015) : mode réaliste (1) ou conventions de jeu (0). Absent : 1 en v4 ; liens v1–v3 rejoués en conventions (0), règles de l'époque. */
  rm?: 0 | 1;
  /** v4 : priorités du joueur pour le bilan (indicateur → poids), seulement celles qui diffèrent du défaut. */
  w?: Record<string, number>;
}

/**
 * Graine d'une nouvelle partie : `?seed=N` (tests, rejouer une partie), sinon param `default_seed`, sinon tirée au hasard
 * (red team v0.2 m3 : avec une graine fixe, tous les joueurs vivaient les mêmes crises). Le moteur reste déterministe : la
 * graine est dans le lien de partage.
 */
export function initialSeed(content: Content): number {
  const q = typeof location !== "undefined" ? new URLSearchParams(location.search).get("seed") : null;
  if (q && /^\d{1,9}$/.test(q)) return Number(q);
  const d = content.params.default_seed?.value;
  if (typeof d === "number" && Number.isFinite(d)) return d;
  const c = globalThis.crypto;
  if (c?.getRandomValues) {
    const a = new Uint32Array(1);
    c.getRandomValues(a);
    return (a[0] ?? 0) % 1_000_000;
  }
  return content.baseline.meta.base_year;
}

type Listener = () => void;

export class Store {
  readonly content: Content;
  /** Contexte moteur ; `measures` = catalogue + mesures dictées `estimé_ia` ajoutées pendant la partie. */
  ctx: EngineContext;
  readonly engine: EngineApi;
  state: GameState;
  private timeline: Timeline | null = null;
  private listeners = new Set<Listener>();

  constructor(content: Content, forceMock = false) {
    this.content = content;
    this.ctx = {
      baseline: content.baseline,
      params: content.params,
      measures: content.measures,
      events: content.events,
      levers: content.levers,
      scorecard: content.scorecard,
      presets: content.presets,
    };
    this.engine = getEngine(this.ctx, forceMock);
    this.state = this.freshState();
    this.bounds = estimateBounds(content);
  }

  /** Avis à afficher après lecture d'un lien (mesure retirée par le filtre de neutralité…). */
  shareNotices: string[] = [];

  /** Hypothèses modifiées ? (affiché « hypothèses modifiées » au résultat). */
  get hypModified(): boolean {
    return Object.keys(this.state.paramOverrides).length > 0;
  }

  /** Reconstruit le contexte moteur avec les hypothèses du joueur. */
  private syncParams() {
    const params = applyOverrides(this.content.params, this.state.paramOverrides);
    if (params !== this.ctx.params) {
      this.ctx = { ...this.ctx, params };
      this.trendCache = null;
    }
  }

  /** Modifie une hypothèse (null = valeur par défaut). Hors fourchette ou non modifiable → ignorée. */
  setParamOverride(id: string, value: number | null) {
    const next = { ...this.state.paramOverrides };
    if (value === null) delete next[id];
    else next[id] = value;
    this.update({ paramOverrides: sanitizeOverrides(next, this.content.params, this.content.paramSources) });
  }

  resetParamOverrides() {
    this.update({ paramOverrides: {} });
  }

  /** Bornes de contrôle des mesures dictées (dérivées du catalogue sourcé, de la baseline et des params). */
  private readonly bounds: EstimateBounds;

  /** Mesures dictées (estimé_ia) ajoutées à la partie. */
  get estimated(): Measure[] {
    return this.content.measures.filter(isIaMeasure);
  }

  /**
   * Ajoute une mesure dictée chiffrée par l'API au catalogue de la partie (le moteur la reçoit dans `measures` comme
   * n'importe quelle mesure) et la coche. Refusée (false) si elle sort de la forme ou des bornes du contenu.
   */
  addEstimated(m: Measure, hyp?: number, select = true): boolean {
    if (checkEstimated(m, this.bounds) !== null) return false;
    if (neutralityHit(m, this.content.politicalNames) !== null) return false;
    if (!this.content.measures.some((x) => x.id === m.id)) {
      this.content.measures = [...this.content.measures, m];
      this.ctx = { ...this.ctx, measures: this.content.measures };
    }
    if (select && !this.state.programme.measure_ids.includes(m.id)) {
      const h = hyp !== undefined && m.hyp && hyp >= m.hyp.low && hyp <= m.hyp.high ? hyp : m.hyp?.default;
      this.update({ programme: { ...this.state.programme, measure_ids: [...this.state.programme.measure_ids, m.id], hyp: { ...this.state.programme.hyp, ...(h !== undefined ? { [m.id]: h } : {}) } } });
      track({ type: "measure_selected", measure_id: m.id, origin: "dictee" });
    } else {
      this.timeline = null;
      this.emit();
    }
    return true;
  }

  freshState(): GameState {
    return {
      programme: { measure_ids: [], hyp: {} },
      financing: { revenue_raises: {}, across_the_board_cut: 0 },
      choices: {},
      seed: initialSeed(this.content),
      voted: false,
      yearIndex: -1,
      kpiView: "public",
      attrMetric: "d_deficit_pct",
      // Les hypothèses modifiées survivent à « Rejouer » (elles décrivent le monde, pas la partie) ; le mode et les priorités aussi.
      paramOverrides: this.state?.paramOverrides ?? {},
      realistic: this.state?.realistic ?? true,
      weights: this.state?.weights ?? {},
      scView: "trend",
      scIndicator: this.content.scorecard?.indicators[0]?.id ?? "",
    };
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    for (const l of this.listeners) l();
  }

  update(patch: Partial<GameState>) {
    this.state = { ...this.state, ...patch };
    this.timeline = null;
    if (patch.paramOverrides) this.syncParams();
    if (patch.realistic !== undefined) this.trendCache = null;
    this.emit();
  }

  reset() {
    this.state = this.freshState();
    this.timeline = null;
    this.shareNotices = [];
    this.emit();
  }

  /** Programme tel que transmis au moteur (variantes natives des mesures cochées). */
  get enginePrg(): Programme {
    return engineProgramme(this.engine, this.state.programme);
  }

  /** Programme moteur pour un programme quelconque (aperçus d'une mesure seule). */
  prg(p: UiProgramme): Programme {
    return engineProgramme(this.engine, p);
  }

  /** Timeline complète (toutes les années du mandat) pour les entrées courantes ; mémorisée jusqu'au prochain update. */
  get timelineAll(): Timeline {
    if (!this.timeline) {
      const s = this.state;
      this.timeline = this.engine.simulate(this.ctx, this.enginePrg, s.financing, s.choices, s.seed, this.mode);
    }
    return this.timeline;
  }

  /** Simule la partie avec une décision supplémentaire (aperçu d'une option de crise). */
  simulateWith(choices: Choices): Timeline {
    const s = this.state;
    return this.engine.simulate(this.ctx, this.enginePrg, s.financing, choices, s.seed, this.mode);
  }

  /** Mode de simulation de la partie (ADR 0015). */
  get mode(): SimMode {
    return { realistic: this.state.realistic };
  }

  private scCache: { key: string; card: Scorecard | null; why?: ScorecardExplanation | null } | null = null;
  private scKey(): string {
    const s = this.state;
    return JSON.stringify([this.enginePrg, s.financing, s.choices, s.seed, s.paramOverrides, s.realistic, this.ctx.measures.length]);
  }
  /** Bilan chiffré comparé (3 simulations) ; null si indisponible (moteur de développement, contenu absent) ou en erreur. */
  scorecard(): Scorecard | null {
    const key = this.scKey();
    if (this.scCache?.key !== key) {
      const s = this.state;
      let card: Scorecard | null = null;
      try {
        card = this.engine.scorecard(this.ctx, this.enginePrg, s.financing, s.choices, s.seed, this.mode);
      } catch (err) {
        console.error("bilan indisponible", err);
      }
      this.scCache = { key, card };
    }
    return this.scCache.card;
  }
  /** « Pourquoi ? » du bilan (≈ 40 simulations : mémorisé, recalculé seulement quand la partie change). */
  scorecardWhy(): ScorecardExplanation | null {
    this.scorecard();
    const c = this.scCache!;
    if (c.why === undefined) {
      const s = this.state;
      try {
        c.why = this.engine.explainScorecard(this.ctx, this.enginePrg, s.financing, s.choices, s.seed, this.mode);
      } catch (err) {
        console.error("explication indisponible", err);
        c.why = null;
      }
    }
    return c.why;
  }

  setRealistic(on: boolean) {
    this.update({ realistic: on });
  }

  /** Priorité du joueur pour un indicateur du bilan (bornée par `scorecard.weights`) ; valeur par défaut = retirée du lien. */
  setWeight(id: string, value: number) {
    const w = this.content.scorecard?.weights;
    if (!w || !this.content.scorecard?.indicators.some((d) => d.id === id) || !Number.isFinite(value)) return;
    const v = Math.max(w.min, Math.min(w.max, value));
    const next = { ...this.state.weights };
    if (v === w.default) delete next[id];
    else next[id] = v;
    this.update({ weights: next });
  }

  resetWeights() {
    this.update({ weights: {} });
  }

  setScView(v: ScView) {
    this.update({ scView: v });
  }

  setScIndicator(id: string) {
    if (this.content.scorecard?.indicators.some((d) => d.id === id)) this.update({ scIndicator: id });
  }

  /** Priorités d'un lien de partage : indicateurs connus, valeurs bornées. */
  sanitizeWeights(raw: unknown): Record<string, number> {
    const sc = this.content.scorecard;
    const out: Record<string, number> = {};
    if (!sc || !raw || typeof raw !== "object") return out;
    for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
      if (typeof v !== "number" || !Number.isFinite(v) || !sc.indicators.some((d) => d.id === id)) continue;
      const x = Math.max(sc.weights.min, Math.min(sc.weights.max, v));
      if (x !== sc.weights.default) out[id] = x;
    }
    return out;
  }

  private trendCache: Timeline | null = null;
  /** Budget tendanciel (programme vide, sans événement). */
  get trend(): Timeline {
    if (!this.trendCache) this.trendCache = this.engine.trend(this.ctx, this.state.seed, this.mode);
    return this.trendCache;
  }

  private attrCache: { key: string; items: AttributionItem[] | null } | null = null;
  /** « Pourquoi ? » mémorisé (≈ 36 simulations : recalculé seulement quand la partie change, pas au tri — red team m2). */
  attribution(): AttributionItem[] | null {
    const s = this.state;
    const key = JSON.stringify([this.enginePrg, s.financing, s.choices, s.seed, s.paramOverrides]);
    if (this.attrCache?.key !== key) this.attrCache = { key, items: this.engine.attribute2032(this.ctx, this.enginePrg, s.financing, s.choices, s.seed) };
    return this.attrCache.items;
  }

  /** Événements déclenchés, dans l'ordre (année puis ordre du moteur). */
  get decisions(): Decision[] {
    const out: Decision[] = [];
    for (const y of this.timelineAll.years) {
      for (const id of y.events_fired) {
        const ev = this.content.events.find((e) => e.id === id);
        if (ev) out.push({ id, year: y.year, index: 0, total: 0, event: ev });
      }
    }
    out.forEach((d, i) => {
      d.index = i + 1;
      d.total = out.length;
    });
    return out;
  }

  /** Prochaine décision sans réponse (null = mandat terminé). */
  get currentDecision(): Decision | null {
    return this.decisions.find((d) => !this.state.choices[d.id]) ?? null;
  }

  get mandateDone(): boolean {
    return this.state.voted && this.currentDecision === null;
  }

  /* --- mutations métier */
  toggleMeasure(id: string) {
    const ids = new Set(this.state.programme.measure_ids);
    const hyp = { ...this.state.programme.hyp };
    const m = this.content.measures.find((x) => x.id === id);
    const origin = m && isIaMeasure(m) ? "dictee" : "catalogue";
    if (ids.has(id)) {
      ids.delete(id);
      track({ type: "measure_deselected", measure_id: id, origin });
    } else {
      ids.add(id);
      if (m?.hyp && hyp[id] === undefined) hyp[id] = m.hyp.default;
      track({ type: "measure_selected", measure_id: id, origin });
    }
    this.update({ programme: { ...this.state.programme, measure_ids: [...ids], hyp } });
  }

  setHyp(id: string, value: number) {
    this.update({ programme: { ...this.state.programme, hyp: { ...this.state.programme.hyp, [id]: value } } });
  }

  /** Variante d'une mesure (`""` = mesure du catalogue). Coche la mesure si elle ne l'est pas. */
  setVariant(measureId: string, variantId: string) {
    const variants = { ...(this.state.programme.variants ?? {}) };
    if (variantId) variants[measureId] = variantId;
    else delete variants[measureId];
    const ids = this.state.programme.measure_ids.includes(measureId) ? this.state.programme.measure_ids : [...this.state.programme.measure_ids, measureId];
    this.update({ programme: { ...this.state.programme, measure_ids: ids, variants } });
  }

  setAttrMetric(m: AttrMetric) {
    this.update({ attrMetric: m });
  }

  setLever(id: string, value: number) {
    const raises = { ...this.state.financing.revenue_raises };
    if (value) raises[id] = value;
    else delete raises[id];
    this.update({ financing: { ...this.state.financing, revenue_raises: raises } });
  }

  setCut(share: number) {
    this.update({ financing: { ...this.state.financing, across_the_board_cut: share } });
  }

  /** Économie ciblée sur un groupe de missions (%, borné par `cut_<groupe>_max_pct`). */
  setMissionCut(group: string, pct: number) {
    const cuts = { ...(this.state.financing.mission_cuts ?? {}) };
    if (pct) cuts[group] = pct;
    else delete cuts[group];
    const fin: UiFinancing = { ...this.state.financing };
    if (Object.keys(cuts).length) fin.mission_cuts = cuts;
    else delete fin.mission_cuts;
    this.update({ financing: fin });
  }

  /**
   * Charge un programme préréglé (ADR 0014 §3) : ses mesures et son financement REMPLACENT le programme en cours (le joueur peut
   * ensuite le modifier). Mesures inconnues du catalogue ignorées ; financement re-borné comme un lien de partage.
   */
  loadPreset(id: string): boolean {
    const p = this.content.presets.find((x) => x.id === id);
    if (!p) return false;
    const known = new Set(this.content.measures.map((m) => m.id));
    const ids = p.measure_ids.filter((m) => known.has(m));
    const hyp: Record<string, number> = {};
    for (const m of this.content.measures) if (ids.includes(m.id) && m.hyp) hyp[m.id] = m.hyp.default;
    const fin = this.clampFinancing(p.financing.revenue_raises, p.financing.across_the_board_cut, p.financing.mission_cuts);
    this.update({ programme: { measure_ids: ids, hyp }, financing: fin, voted: false, yearIndex: -1, choices: {} });
    return true;
  }

  vote() {
    this.update({ voted: true, yearIndex: 0 });
  }

  /** Revenir au programme après le vote : les décisions de crise sont effacées (elles dépendaient du budget). */
  unvote() {
    this.update({ voted: false, yearIndex: -1, choices: {} });
  }

  choose(eventId: string, optionId: string) {
    this.update({ choices: { ...this.state.choices, [eventId]: optionId } });
  }

  setKpiView(v: "public" | "expert") {
    this.update({ kpiView: v });
  }

  toShare(): SharePayload {
    const s = this.state;
    const table = codeTable(this.content.measures.map((m) => m.id));
    const code = (id: string) => (table ? idCode(id) : id);
    const out: SharePayload = {
      v: table ? 4 : 2,
      p: s.programme.measure_ids.map(code),
      h: Object.fromEntries(Object.entries(s.programme.hyp).map(([id, v]) => [code(id), v])),
      f: s.financing.revenue_raises,
      c: s.financing.across_the_board_cut,
      ch: s.choices,
      s: s.seed,
      y: s.voted ? Math.max(0, this.timelineAll.years.length - 1) : -1,
    };
    if (s.programme.variants && Object.keys(s.programme.variants).length) {
      out.va = Object.fromEntries(Object.entries(s.programme.variants).map(([mid, vid]) => [code(mid), table ? idCode(vid) : vid]));
    }
    const ia = this.estimated.filter((m) => s.programme.measure_ids.includes(m.id)).slice(0, MAX_SHARED_ESTIMATES);
    if (ia.length) out.ia = ia.map(encodeEstimate);
    if (s.financing.mission_cuts && Object.keys(s.financing.mission_cuts).length) out.m = s.financing.mission_cuts;
    if (Object.keys(s.paramOverrides).length) out.hp = s.paramOverrides;
    out.rm = s.realistic ? 1 : 0;
    if (Object.keys(s.weights).length) out.w = s.weights;
    return out;
  }

  /**
   * Financement d'un lien de partage ramené dans les bornes des curseurs (`lev_<id>_min/max_bn`, `cut_max_share`) :
   * le moteur ne valide pas les bornes lui-même et un lien forgé pouvait porter n'importe quel montant (red team).
   */
  clampFinancing(raises: Record<string, number>, cut: number, missionCuts?: Record<string, number>): UiFinancing {
    const bound = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));
    const out: Record<string, number> = {};
    for (const lv of this.content.levers) {
      const v = raises[lv.id];
      if (typeof v !== "number" || !Number.isFinite(v) || v === 0) continue;
      out[lv.id] = bound(v, lv.min.value ?? 0, lv.max.value ?? 0);
    }
    const cutMax = this.content.params.cut_max_share?.value ?? 0;
    const fin: UiFinancing = { revenue_raises: out, across_the_board_cut: Number.isFinite(cut) ? bound(cut, 0, cutMax) : 0 };
    // Économies par mission : groupes connus des params (`cut_<groupe>_max_pct`) seulement, bornés à [0, max].
    const mc: Record<string, number> = {};
    for (const g of missionCutGroups(this.content.params)) {
      const v = missionCuts?.[g.id];
      if (typeof v === "number" && Number.isFinite(v) && v > 0) mc[g.id] = bound(v, 0, g.max);
    }
    if (Object.keys(mc).length) fin.mission_cuts = mc;
    return fin;
  }

  fromShare(d: SharePayload) {
    this.shareNotices = [];
    // Mesures dictées du lien : re-contrôlées (forme + bornes du contenu + filtre de neutralité) ; sinon ignorées.
    for (const x of (Array.isArray(d.ia) ? d.ia : []).slice(0, MAX_SHARED_ESTIMATES)) {
      const m = decodeEstimate(x, this.bounds);
      if (!m) continue;
      const hit = neutralityHit(m, this.content.politicalNames);
      if (hit !== null) {
        const msg = neutralityNotice(hit);
        if (!this.shareNotices.includes(msg)) this.shareNotices.push(msg);
        continue;
      }
      this.addEstimated(m, undefined, false);
    }
    // v3 : codes opaques → identifiants du catalogue chargé (mesures dictées comprises) ; v1/v2 : identifiants en clair.
    const ids = this.content.measures.map((m) => m.id);
    const table = (d.v ?? 1) >= 3 ? codeTable(ids) : null;
    const toId = (k: string): string | undefined => (table ? table.get(k) : ids.includes(k) ? k : undefined);
    const hyp: Record<string, number> = {};
    for (const [k, v] of Object.entries(d.h ?? {})) {
      const id = toId(k);
      if (id && typeof v === "number" && Number.isFinite(v)) hyp[id] = v;
    }
    // Variantes : seulement celles qui existent au catalogue (lien forgé ou catalogue modifié depuis).
    const va: Record<string, string> = {};
    for (const [k, vk] of Object.entries(d.va ?? {})) {
      const mid = toId(k);
      const m = mid ? this.content.measures.find((x) => x.id === mid) : undefined;
      const vid = table ? m?.variants?.find((x) => idCode(x.id) === vk)?.id : vk;
      if (m && vid && variantCost(m, vid) !== undefined) va[m.id] = vid;
    }
    const measureIds = (Array.isArray(d.p) ? d.p : []).map((k) => (typeof k === "string" ? toId(k) : undefined)).filter((id): id is string => !!id);
    const fresh = this.freshState();
    this.state = {
      ...fresh,
      programme: { measure_ids: [...new Set(measureIds)], hyp, ...(Object.keys(va).length ? { variants: va } : {}) },
      financing: this.clampFinancing(d.f ?? {}, d.c ?? 0, d.m),
      choices: d.ch ?? {},
      seed: typeof d.s === "number" && Number.isFinite(d.s) ? d.s : fresh.seed,
      voted: (d.y ?? -1) >= 0,
      yearIndex: d.y ?? -1,
      paramOverrides: sanitizeOverrides(d.hp, this.content.params, this.content.paramSources),
      // v4 : mode et priorités du lien ; v1–v3 : rejoués avec les règles de l'époque (conventions de jeu, ADR 0015 §5).
      realistic: d.rm !== undefined ? d.rm !== 0 : (d.v ?? 1) >= 4,
      weights: this.sanitizeWeights(d.w),
    };
    this.timeline = null;
    this.trendCache = null;
    this.syncParams();
    this.emit();
  }
}
